import { useCustom, useTranslate } from '@refinedev/core';
import { Table, Typography } from 'antd';
import { enUS, ru } from 'date-fns/locale';
import React from 'react';
import { formatDay } from '../caseload/formatDay';

/**
 * E57 · ClientMeditations — Meditations as a section of the client page: "the only per-client tool section with no portal
 * page today: the sessions this client listened to (title, voice, minutes, date), newest first, read from All records with
 * the client and the tool set" (cc-practice, screen e57e0001-…-080).
 *
 * Behavior lines implemented here:
 *   · empty-state: no meditation yet for this client (clientPage.meditations.empty);
 *   · format: minutes listened and date per row (clientPage.meditations.row).
 * The voice is part of the meditation's title as the catalogue names it; minutes are the seconds the player really
 * advanced (a recovered finish has none and shows "—").
 */
type Row = {
  recordId: string;
  occurredAt: string;
  summary?: string | null;
  metric?: { kind: string; value: number } | null;
};

export const ClientMeditationsSection: React.FC<{ clientId: string; locale: 'en' | 'ru' }> = ({ clientId, locale }) => {
  const t = useTranslate();
  const { data, isLoading } = useCustom<{ rows: Row[]; count: number }>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'allRecords',
      fields: ['count', { rows: ['recordId', 'occurredAt', 'summary', { metric: ['kind', 'value'] }] }],
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
        tools: { name: 'tools', type: '[String!]', value: ['meditations'] },
        limit: { name: 'limit', type: 'Int', value: 200 },
      },
    },
    queryOptions: { enabled: Boolean(clientId) },
  });
  const rows = data?.data?.rows ?? [];
  const dateLocale = locale === 'ru' ? ru : enUS;
  return (
    <section data-testid="cc-client-meditations">
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {t('learn.tools.Meditations')}
      </Typography.Title>
      <Table<Row>
        rowKey="recordId"
        loading={isLoading}
        dataSource={rows}
        pagination={rows.length > 20 ? { pageSize: 20 } : false}
        locale={{ emptyText: <span data-testid="cc-client-meditations-empty">{t('clientPage.meditations.empty')}</span> }}
        data-testid="cc-client-meditations-table"
        columns={[
          {
            key: 'date',
            title: t('clientPage.meditations.date'),
            render: (_v, r) => formatDay(r.occurredAt, locale === 'ru' ? 'EEEEEE d MMM yyyy' : 'EEE d MMM yyyy', dateLocale),
          },
          { key: 'meditation', title: t('clientPage.meditations.meditation'), render: (_v, r) => r.summary ?? '—' },
          {
            key: 'minutes',
            title: t('clientPage.meditations.minutes'),
            render: (_v, r) =>
              r.metric?.kind === 'minutes' ? t('clientPage.meditations.row', { minutes: r.metric.value }) : '—',
          },
        ]}
      />
    </section>
  );
};
